/**
 * Map style sub-FAB.
 *
 * Opens a MiniModal that lets the user pick:
 *   - the map appearance mode (light / dark / auto),
 *   - a layer override (default / hybrid / legacy).
 *
 * The burst plumbing (anchor, auto-close on open, a11y attributes) is
 * provided by `SubFloatingAction`; this file only carries the
 * map-specific option sets and handlers.
 */

import React, { useCallback, useMemo } from "react";
import mapStyleIcon from "../../../../image/icons/floating/MapStyleIcon.svg";
import {
    MAP_APPEARANCE_MODE_ICONS,
    MAP_STYLE_ID_ICONS,
    MAP_STYLE_OVERRIDE_ICONS,
    type MapAppearanceMode,
    type MapStyleId,
    type MapStyleOverride,
} from "../../../../types/MapStyle";
import ImageOptionSelector, { type ImageSelectorOption } from "../../../MiniModal/ImageOptionSelector";
import MiniModalSection from "../../../MiniModal/MiniModalSection";
import SubFloatingAction from "./SubFloatingAction";

/** Appearance-mode option descriptor. */
interface ModeOption {
    "id":    MapAppearanceMode;
    "label": string;
}

/** Style-override option descriptor. */
interface OverrideOption {
    "id":    MapStyleOverride;
    "label": string;
}

/** Supported appearance modes (source of truth for the UI). */
const MODE_OPTIONS: readonly ModeOption[] = [
    {
        "id":    "light",
        "label": "Clair",
    },
    {
        "id":    "dark",
        "label": "Sombre",
    },
    {
        "id":    "auto",
        "label": "Auto",
    },
];

/** Supported layer overrides (source of truth for the UI). */
const OVERRIDE_OPTIONS: readonly OverrideOption[] = [
    {
        "id":    "default",
        "label": "Defaut",
    },
    {
        "id":    "hybrid",
        "label": "Hybride",
    },
    {
        "id":    "legacy",
        "label": "Classique",
    },
];

/**
 * Type guard for `MapAppearanceMode` coming out of generic selectors.
 *
 * @param value Candidate string.
 * @returns `true` when `value` is a supported appearance mode.
 */
const isMapAppearanceMode = ( value: string ): value is MapAppearanceMode => {
    return MODE_OPTIONS.some( ( modeOption ) => modeOption.id === value );
};

/**
 * Type guard for `MapStyleOverride` coming out of generic selectors.
 *
 * @param value Candidate string.
 * @returns `true` when `value` is a supported style override.
 */
const isMapStyleOverride = ( value: string ): value is MapStyleOverride => {
    return OVERRIDE_OPTIONS.some( ( overrideOption ) => overrideOption.id === value );
};

/** Props exposed by the MapStyle sub-FAB. */
export interface MapStyleSubFloatingActionProps {
    "appearanceMode":         MapAppearanceMode;
    "styleOverride":          MapStyleOverride;
    "defaultStyleId":         MapStyleId;
    "isDarkUi":               boolean;
    "onAppearanceModeChange": ( mode: MapAppearanceMode ) => void;
    "onStyleOverrideChange":  ( styleOverride: MapStyleOverride ) => void;

    /** Optional listener fired whenever the MiniModal open state changes. */
    "onOpenStateChange"?: ( isOpen: boolean ) => void;
}

/**
 * Render the Map-Style sub-FAB with its MiniModal panel.
 *
 * @param props Component props. See `MapStyleSubFloatingActionProps`.
 * @returns The configured `SubFloatingAction` with the map-style MiniModal.
 */
const MapStyleSubFloatingAction: React.FC<MapStyleSubFloatingActionProps> = ( {
    appearanceMode,
    styleOverride,
    defaultStyleId,
    isDarkUi,
    onAppearanceModeChange,
    onStyleOverrideChange,
    onOpenStateChange,
} ) => {
    /**
     * Validate and propagate an appearance-mode change.
     *
     * @param mode Candidate mode string from the selector.
     */
    const handleModeChange = useCallback( ( mode: string ): void => {
        if ( !isMapAppearanceMode( mode ) ) {
            return;
        }

        onAppearanceModeChange( mode );
    }, [ onAppearanceModeChange ] );

    /**
     * Validate and propagate a layer-override change.
     *
     * @param override Candidate override string from the selector.
     */
    const handleStyleOverrideChange = useCallback( ( override: string ): void => {
        if ( !isMapStyleOverride( override ) ) {
            return;
        }

        onStyleOverrideChange( override );
    }, [ onStyleOverrideChange ] );

    const modeSelectorOptions = useMemo<readonly ImageSelectorOption[]>( () => {
        return MODE_OPTIONS.map( ( modeOption ) => {
            return {
                "id":       modeOption.id,
                "label":    modeOption.label,
                "imageSrc": MAP_APPEARANCE_MODE_ICONS[modeOption.id],
            };
        } );
    }, [] );

    const styleOverrideOptions = useMemo<readonly ImageSelectorOption[]>( () => {
        return OVERRIDE_OPTIONS.map( ( overrideOption ) => {
            return {
                "id":       overrideOption.id,
                "label":    overrideOption.label,
                "imageSrc": overrideOption.id === "default"
                    ? MAP_STYLE_ID_ICONS[defaultStyleId]
                    : MAP_STYLE_OVERRIDE_ICONS[overrideOption.id],
            };
        } );
    }, [ defaultStyleId ] );

    return (
        <SubFloatingAction
            ariaLabel="Open map style selector"
            iconAlt="Map style"
            iconSrc={mapStyleIcon}
            isDarkUi={isDarkUi}
            kind="modal"
            onOpenStateChange={onOpenStateChange}
        >
            <MiniModalSection
                isDarkUi={isDarkUi}
                title="Mode"
            >
                <ImageOptionSelector
                    isDarkUi={isDarkUi}
                    onChange={handleModeChange}
                    options={modeSelectorOptions}
                    selectionType="radio"
                    value={appearanceMode}
                />
            </MiniModalSection>
            <MiniModalSection
                isDarkUi={isDarkUi}
                title="Carte"
            >
                <ImageOptionSelector
                    isDarkUi={isDarkUi}
                    onChange={handleStyleOverrideChange}
                    options={styleOverrideOptions}
                    selectionType="radio"
                    value={styleOverride}
                />
            </MiniModalSection>
        </SubFloatingAction>
    );
};

export default MapStyleSubFloatingAction;
